import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, 
  CreditCard, 
  Shield, 
  Camera, 
  FileCheck, 
  ChevronRight,
  Zap,
  Check
} from 'lucide-react';

const products = [
  {
    id: 'ova-dispatch',
    name: 'OVA Dispatch Engine',
    tagline: 'Replaces RideOn/NEMT Routes',
    icon: Brain,
    color: 'cyan',
    link: '/dispatch',
    description: 'AI-powered conversational dispatch that understands natural language, handles complex multi-leg trips, and optimizes routing in real-time.',
    features: [
      'Voice & text-based trip requests',
      'Multi-language support',
      'Intelligent trip matching',
      'Real-time route optimization',
      'Automated scheduling',
    ],
    integration: 'Direct API integration with existing systems'
  },
  {
    id: 'medex-pay',
    name: 'Medex Pay',
    tagline: 'Financial Infrastructure',
    icon: CreditCard,
    color: 'blue',
    link: '/payments',
    description: 'Complete payment processing for healthcare transportation with support for insurance billing, direct payments, and instant driver payouts.',
    features: [
      '837 & CMS1500 integrations',
      'Automated claims processing',
      'Instant driver payouts',
      'Revenue analytics',
      'Multi-payer support',
    ],
    integration: 'PCI-compliant payment gateway'
  },
  {
    id: 'group-insurance',
    name: 'Medex Group Insurance',
    tagline: 'Risk Management',
    icon: Shield,
    color: 'purple',
    link: '/insurance',
    description: 'Pay As You Go insurance designed for NEMT. Only pay when your vehicles are on active trips. Save 30-50% vs traditional premiums.',
    features: [
      'Pay per mile, not per month',
      'Workers compensation',
      'Liability protection',
      'No long-term contracts',
      'Quick claims processing',
    ],
    integration: 'Automatic policy management'
  },
  {
    id: 'nemt-vision',
    name: 'NEMT Vision AI Cameras',
    tagline: 'Safety & Compliance',
    icon: Camera,
    color: 'green',
    link: '/cameras',
    description: 'AI-powered dash cameras that monitor driver behavior, ensure passenger safety, and provide real-time alerts for incidents.',
    features: [
      'Real-time AI monitoring',
      'Driver behavior scoring',
      'Incident detection & recording',
      'GPS integration',
      'Cloud storage & retrieval',
    ],
    integration: 'Plug-and-play hardware'
  },
  {
    id: 'credentialing',
    name: 'Centralized Credentialing',
    tagline: 'Compliance Automation',
    icon: FileCheck,
    color: 'cyan',
    link: '/credentialing',
    description: 'Automated credential management that tracks licenses, certifications, and compliance requirements across your entire fleet.',
    features: [
      'Document verification',
      'Expiration tracking',
      'Automated renewal reminders',
      'Audit-ready reports',
      'Background check integration',
    ],
    integration: 'State DMV & registry connections'
  },
];

export const ProductStackSection = () => {
  const [expandedProduct, setExpandedProduct] = useState(null);
  const navigate = useNavigate();

  const colorClasses = {
    cyan: {
      bg: 'bg-neon-cyan/10',
      border: 'border-neon-cyan/30',
      text: 'text-neon-cyan',
      glow: 'shadow-glow-cyan',
    },
    blue: {
      bg: 'bg-neon-blue/10',
      border: 'border-neon-blue/30',
      text: 'text-neon-blue',
      glow: 'shadow-glow-blue',
    },
    purple: {
      bg: 'bg-neon-purple/10',
      border: 'border-neon-purple/30',
      text: 'text-neon-purple',
      glow: 'shadow-glow-purple',
    },
    green: {
      bg: 'bg-neon-green/10',
      border: 'border-neon-green/30',
      text: 'text-neon-green',
      glow: 'shadow-[0_0_20px_-5px_rgba(16,185,129,0.4)]',
    },
  };

  return (
    <section 
      id="products" 
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="products-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-medex-bg via-medex-surface/20 to-medex-bg" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1 rounded-full bg-white/5 border border-white/10 text-sm font-mono text-slate-400 uppercase tracking-wider mb-6">
            Product Stack
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            Infrastructure Layer
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-400">
            Modular products that work together as a unified ecosystem or standalone solutions.
          </p>
        </motion.div>

        {/* Products Grid */}
        <div className="space-y-4">
          {products.map((product, index) => {
            const colors = colorClasses[product.color];
            const isExpanded = expandedProduct === product.id;
            
            return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`
                  glass-card overflow-hidden cursor-pointer
                  transition-all duration-500
                  ${isExpanded ? `${colors.border} ${colors.glow}` : 'hover:border-white/20'}
                `}
                onClick={() => setExpandedProduct(isExpanded ? null : product.id)}
                data-testid={`product-${product.id}`}
              >
                {/* Header */}
                <div className="p-6 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-xl ${colors.bg} ${colors.border} border`}>
                      <product.icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-bold text-white">
                        {product.name}
                      </h3>
                      <p className="text-sm text-slate-500">{product.tagline}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`hidden sm:block text-xs font-mono ${colors.text} px-3 py-1 rounded-full ${colors.bg}`}>
                      {product.integration}
                    </span>
                    <ChevronRight className={`w-5 h-5 text-slate-500 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} />
                  </div>
                </div>

                {/* Expanded Content */}
                <motion.div
                  initial={false}
                  animate={{ 
                    height: isExpanded ? 'auto' : 0,
                    opacity: isExpanded ? 1 : 0
                  }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 pt-2 border-t border-white/5">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-slate-300 leading-relaxed mb-4">
                          {product.description}
                        </p>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(product.link);
                          }}
                          className={`inline-flex items-center text-sm ${colors.text} hover:underline`}
                        >
                          Learn more <ChevronRight className="w-4 h-4 ml-1" />
                        </button>
                      </div>
                      <div>
                        <h4 className="text-sm font-mono text-slate-500 uppercase tracking-wider mb-3">
                          Key Features
                        </h4>
                        <ul className="space-y-2">
                          {product.features.map((feature, i) => (
                            <li key={i} className="flex items-center space-x-2 text-sm text-slate-400">
                              <Check className={`w-4 h-4 ${colors.text}`} />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* Integration Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center space-x-2 text-sm text-slate-500">
            <Zap className="w-4 h-4 text-neon-cyan" />
            <span>All products integrate seamlessly with the MedexOVA ecosystem</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductStackSection;
